import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { Logo } from "./Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHIPS = [
  "¿Qué servicios ofrecéis?",
  "¿Trabajáis con administraciones públicas?",
  "He sufrido un ataque, ¿qué hago?",
];

const WELCOME = "Hola, soy el asistente de Cibermedida. Puedo resolver tus dudas sobre inteligencia artificial, ciberseguridad y nuestros servicios. ¿En qué te ayudo?";

const getSessionId = () => {
  let id = localStorage.getItem("ciber_chat_session");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("ciber_chat_session", id);
  }
  return id;
};

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: WELCOME }]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    if (open) document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const resp = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), message: msg }),
      });
      if (!resp.ok || !resp.body) throw new Error("Chat request failed");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6).trim();
          if (payload === "[DONE]") continue;
          try {
            const parsed = JSON.parse(payload);
            if (parsed.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + parsed.delta };
                return copy;
              });
            } else if (parsed.error) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: parsed.error };
                return copy;
              });
            }
          } catch { /* línea parcial */ }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "No se pudo conectar con el asistente. Inténtalo de nuevo en unos minutos." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <motion.button
        data-testid="chat-open-button"
        onClick={() => setOpen(!open)}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1.6, type: "spring", stiffness: 200 }}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#3FE0F2] text-[#14163C] flex items-center justify-center shadow-[0_8px_30px_rgba(63,224,242,0.4)] hover:bg-[#7FEBF8] hover:scale-105 transition-[background-color,transform] duration-300"
        aria-label={open ? "Cerrar asistente" : "Abrir asistente"}
        aria-expanded={open}
        aria-controls="chat-panel"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            id="chat-panel"
            role="dialog"
            aria-modal="false"
            aria-labelledby="chat-title"
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-6 z-50 w-[92vw] max-w-sm bg-[#242764] border border-[#3FE0F2]/30 rounded-2xl shadow-2xl overflow-hidden flex flex-col"
            style={{ height: "min(520px, 70vh)" }}
          >
            <div className="flex items-center gap-3 px-5 py-4 bg-[#1B1E4E] border-b border-[#3FE0F2]/15">
              <Logo size={30} />
              <div className="flex-1">
                <p id="chat-title" className="font-heading text-sm font-semibold text-[#EEFBFF]">Asistente Cibermedida</p>
                <p className="flex items-center gap-1.5 text-[11px] text-[#7D86C2]">
                  <span className="pulse-dot inline-block w-1.5 h-1.5 rounded-full bg-[#06D6A0]" />
                  En línea
                </p>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chat-messages" aria-live="polite" aria-atomic="false">
              {messages.map((m, i) => (
                <div
                  key={i}
                  data-testid={`chat-message-${m.role}-${i}`}
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "ml-auto bg-[#3FE0F2] text-[#14163C] rounded-br-sm"
                      : "bg-[#1B1E4E] text-[#EEFBFF] border border-[#3FE0F2]/15 rounded-bl-sm"
                  }`}
                >
                  {m.content || (streaming && i === messages.length - 1 ? "…" : "")}
                </div>
              ))}
              {messages.length === 1 && !streaming && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {CHIPS.map((c, i) => (
                    <button
                      key={c}
                      data-testid={`chat-chip-${i}`}
                      onClick={() => send(c)}
                      className="text-xs text-[#3FE0F2] border border-[#3FE0F2]/30 rounded-full px-3 py-1.5 hover:bg-[#3FE0F2]/10 transition-colors duration-300"
                    >
                      {c}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="flex items-center gap-2 px-4 py-3 bg-[#1B1E4E] border-t border-[#3FE0F2]/15"
            >
              <input
                ref={inputRef}
                id="chat-input"
                name="message"
                aria-label="Escribe tu pregunta"
                autoComplete="off"
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Escribe tu pregunta…"
                maxLength={1000}
                className="flex-1 bg-[#242764] border border-[#3FE0F2]/20 rounded-full px-4 py-2.5 text-sm text-[#EEFBFF] placeholder:text-[#7D86C2] focus:outline-none focus:border-[#3FE0F2] transition-colors"
              />
              <button
                type="submit"
                data-testid="chat-send-button"
                disabled={streaming || !input.trim()}
                className="w-10 h-10 rounded-full bg-[#3FE0F2] text-[#14163C] flex items-center justify-center hover:bg-[#7FEBF8] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
                aria-label="Enviar mensaje"
              >
                {streaming ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
